import { keyframes, styled } from 'styled-components';
import { Variant, Size } from '../../types';
import {
    IGNORE_BUTTON_PROPS,
    buttonBackgroundColor,
    buttonBorder,
    buttonColor,
} from './button.utils';
import { ButtonOptionsProps, ButtonProps } from '.';

// Exported for ActionRow, which overrides its flex: see action-row.styles.ts.
export const BaseButton = styled.button<ButtonProps>`
    display: flex;
    flex: ${({ fillParent }) => (fillParent ? 'initial' : 0)};
    cursor: ${(props) => (props.disabled || props.loading ? 'not-allowed' : 'pointer')};
    & * {
        pointer-events: none;
    }
`;

const isA11yVariant = (variant: Variant | undefined) => variant === Variant.A11Y;

export const ButtonText = styled.span``;
export const ChildrenContainer = styled.span``;

export const StyledButton = styled(BaseButton).withConfig({
    shouldForwardProp: (prop) => !IGNORE_BUTTON_PROPS.includes(prop),
})`
    align-items: center;
    justify-content: ${({ variant }) => (isA11yVariant(variant) ? 'start' : 'center')};
    flex-direction: row;
    box-sizing: border-box;
    gap: ${({ theme }) => theme.spacing(1)};
    white-space: nowrap;
    text-align: ${({ variant }) => (isA11yVariant(variant) ? 'left' : 'center')};
    background-color: ${buttonBackgroundColor};
    color: ${buttonColor};
    border: ${buttonBorder};
    border-radius: ${({ theme, size = Size.NORMAL }) => theme.componentSizes.button[size].radius}px;
    font-family: ${(props) => props.theme.fontBase};
    font-weight: 600;
    font-size: ${({ theme, size = Size.NORMAL }) => theme.componentSizes.button[size].fontSize}px;
    height: ${({ theme, size = Size.NORMAL }) => theme.componentSizes.button[size].height}px;
    ${({ fillParent }) => fillParent && 'width: 100%;'};

    ${ChildrenContainer} {
        display: flex;
        justify-content: ${({ variant }) => (isA11yVariant(variant) ? 'start' : 'center')};
        flex-direction: row;
        gap: ${({ theme }) => theme.spacing(1)};
    }

    padding: ${({ padding, variant, size, theme }) => {
        if (padding) {
            return padding;
        }
        if (isA11yVariant(variant)) {
            return '0px';
        }
        const buttonStyles = theme.componentSizes.button[size ?? Size.NORMAL];

        return `${buttonStyles.verticalPadding}px ${buttonStyles.horizontalPadding}px`;
    }};

    @media print {
        ${({ theme, showInPrint }) => {
            if (showInPrint) {
                return `
          background: none;
          border: none;
          color: ${theme.textColor};
          padding: 0;
        `;
            }

            return 'display: none';
        }};
    }

    &[href] {
        text-decoration: none;
    }
`;

export enum MarginDirection {
    LEFT = 'left',
    RIGHT = 'right',
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
    background: linear-gradient(
        to right,
        ${({ theme }) => theme.progressSpinnerColor},
        transparent
    );
    border-radius: 50%;
    width: 12px;
    height: 12px;
    mask: radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px));
    -webkit-mask: radial-gradient(
        farthest-side,
        transparent calc(100% - 3px),
        black calc(100% - 3px)
    );
    animation: ${spin} 2s linear infinite;
    margin-right: ${(props) => props.theme.spacing(2)};
`;

export const IconWrapper = styled.div<{ $marginDirection: MarginDirection; $useMargin: boolean }>`
    justify-content: center;
    display: flex;
    ${({ $marginDirection, $useMargin, theme }) =>
        $marginDirection === MarginDirection.LEFT
            ? `margin-left: ${theme.spacing($useMargin ? 2 : 0)};`
            : `margin-right: ${theme.spacing($useMargin ? 2 : 0)};`}
    pointer-events: none;

    @media print {
        display: none;
    }
`;

export const ButtonOption = styled(BaseButton).withConfig({
    shouldForwardProp: (prop) => !IGNORE_BUTTON_PROPS.includes(prop),
})<Omit<ButtonOptionsProps, 'title'>>`
    flex-direction: row;
    background-color: ${({ theme }) => theme.buttonTextColorPrimary};
    border: none;
    outline: ${({ selected, theme }) =>
        selected ? `${theme.spacing(0.5)} solid ${theme.buttonColorPrimary}` : 'none'};
    position: relative;
    border-radius: ${({ theme }) => theme.spacing(3)};
    ${({ fillParent }) => fillParent && 'width: 100%;'};
    padding: ${({ theme }) => theme.spacing(6)};
    gap: ${({ theme }) => theme.spacing(6)};
    ${(props) =>
        props.disabled
            ? `
          background-color: ${props.theme.buttonColorDisabled};
          color: ${props.theme.buttonTextColorDisabled};
        `
            : ''};
`;

export const ButtonOptionSelected = styled.div`
    position: absolute;
    z-index: 100;
    top: 16px;
    right: 16px;
`;

export const ButtonOptionTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    /* The button's own text-align is center, which only shows once a title wraps. */
    text-align: start;
`;

type StyledDropdownButtonProps = Pick<ButtonProps, 'fillParent' | 'size'> & { error?: boolean };
export const StyledDropdownButton = styled.button.withConfig({
    shouldForwardProp: (prop) => !IGNORE_BUTTON_PROPS.includes(prop),
})<StyledDropdownButtonProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    box-sizing: border-box;
    border: ${({ error, theme }) => (error ? `2px solid ${theme.errorColor}` : 'none')};
    border-radius: ${({ theme, size = Size.NORMAL }) =>
        theme.componentSizes.dropdown[size].radius}px;
    background: ${({ theme }) => theme.inputBackground};
    width: ${({ fillParent }) => (fillParent ? '100%' : 'auto')};
    height: ${({ theme, size = Size.NORMAL }) => theme.componentSizes.dropdown[size].height}px;
    padding: ${({ theme, size = Size.NORMAL }) => {
        const s = theme.componentSizes.dropdown[size];
        return `${s.verticalPadding}px ${s.horizontalPadding}px`;
    }};
    font-size: ${({ theme, size = Size.NORMAL }) => theme.componentSizes.button[size].fontSize}px;
    cursor: pointer;

    &:after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: ${({ theme }) => theme.spacing(3)};
        border: ${({ theme }) => theme.inputBorder};
        pointer-events: none;
    }

    &:focus-visible {
        outline: none;
        background: ${({ theme }) => theme.inputActiveBackground};
    }
    &:focus-visible:after {
        border: ${({ theme }) => theme.inputBorderActive};
    }

    & ${ButtonText} {
        position: absolute;
        z-index: 1;
        left: ${({ theme }) => theme.spacing(4)};
        top: ${({ theme }) => theme.spacing(5)};
        max-width: calc(100% - ${({ theme }) => theme.spacing(12)});
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        color: ${({ theme }) => theme.inputLabelColor};
        font-size: ${({ theme }) => theme.spacing(3.5)};
        line-height: ${({ theme }) => theme.lineHeight ?? theme.spacing(5)};
        pointer-events: none;
        transition: all 0.2s ease-in-out;
    }

    & ${ChildrenContainer}:not(:empty) ~ ${ButtonText}, &:focus-visible ${ButtonText} {
        left: ${({ theme }) => theme.spacing(5)};
        top: ${({ theme }) => theme.spacing(2.25)};
        font-size: ${({ theme }) => theme.spacing(2.75)};
    }

    & ${ChildrenContainer} {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        color: ${({ theme }) => theme.inputInputColor || '#000'};
        font-family: ${({ theme }) => theme.fontBase};
        font-size: ${({ theme, size = Size.NORMAL }) =>
            theme.componentSizes.button[size].fontSize}px;
        font-weight: 400;
        padding-left: 0;
        padding-right: ${({ theme }) => theme.spacing(9)};
        padding-top: ${({ theme, size = Size.NORMAL }) =>
            size === Size.TINY ? theme.spacing(0.5) : theme.spacing(0.75)};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%; /* allow long values to shrink and ellipsize */
        line-height: 1.25;
    }

    & ${IconWrapper} {
        position: absolute;
        top: 0;
        right: ${({ theme }) => theme.spacing(4)};
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 2;
    }
`;
